import { useCallback, useState } from 'react';
import { Printer, RotateCcw, ArrowLeft, Download, Copy, Check, FoldVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCard } from '@/contexts/CardContext';
import { CardPreview } from '@/components/CardPreview';
import { formatAmount } from '@/lib/cashu';
import { toast } from '@/hooks/useToast';

export function Step3Print() {
  const { card, setStep, resetCard } = useCard();
  const [copied, setCopied] = useState(false);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleCopyToken = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(card.cashuToken);
      setCopied(true);
      toast({
        title: 'Token copied!',
        description: 'Cashu token copied to clipboard.',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = card.cashuToken;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [card.cashuToken]);

  const handleDownloadQR = useCallback(() => {
    if (!card.qrDataUrl) return;
    const link = document.createElement('a');
    link.download = `cashu-card-${card.amount}-${card.unit}.png`;
    link.href = card.qrDataUrl;
    link.click();
  }, [card]);

  return (
    <div className="animate-in fade-in-0 slide-in-from-right-4 duration-500">
      <div className="text-center space-y-3 mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/25">
          <Printer className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight">Preview & Print</h2>
        <p className="text-muted-foreground max-w-lg mx-auto">
           Your foldable greeting card is ready! Print on both sides, then fold in half.
        </p>
      </div>

      <div className="max-w-5xl mx-auto space-y-8">
        {/* Card Preview - both sides */}
        <CardPreview card={card} side="both" />

        {/* Info bar */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 text-sm font-medium border border-amber-200 dark:border-amber-800">
            <span className="text-lg">&#8383;</span>
            {formatAmount(card.amount, card.unit)}
          </div>
          {card.recipientName && (
            <div className="px-3 py-1.5 rounded-full bg-muted text-sm">
              To: <span className="font-medium">{card.recipientName}</span>
            </div>
          )}
          {card.senderName && (
            <div className="px-3 py-1.5 rounded-full bg-muted text-sm">
              From: <span className="font-medium">{card.senderName}</span>
            </div>
          )}
        </div>

        {/* Folding instructions */}
        <div className="max-w-md mx-auto">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/50">
            <FoldVertical className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            <div className="text-sm space-y-1">
              <p className="font-semibold text-emerald-700 dark:text-emerald-300">How to Print & Fold</p>
              <ol className="text-muted-foreground text-xs leading-relaxed list-decimal list-inside space-y-0.5">
                <li>Print both pages <strong>double-sided</strong> (flip on short edge)</li>
                <li>Fold the sheet in half so the front image faces out</li>
                <li>Your greeting card (5&quot; &times; 7&quot;) is ready!</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-xl mx-auto">
          <Button
            onClick={handlePrint}
            size="lg"
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25 col-span-2 md:col-span-2"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Card
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadQR}
            disabled={!card.qrDataUrl}
          >
            <Download className="w-4 h-4 mr-2" />
            QR
          </Button>
          <Button
            variant="outline"
            onClick={handleCopyToken}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2 text-emerald-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Token
              </>
            )}
          </Button>
        </div>

        {/* Back / New Card */}
        <div className="flex gap-3 justify-center">
          <Button variant="ghost" onClick={() => setStep(2)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Edit Design
          </Button>
          <Button variant="ghost" onClick={resetCard}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Create New Card
          </Button>
        </div>
      </div>
    </div>
  );
}
